import React from "react";
import PersonIcon from "@material-ui/icons/Person";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import EditIcon from "@material-ui/icons/Edit";
import DirectionsBoatIcon from "@material-ui/icons/DirectionsBoat";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
  ActionButtons, ActionGrid, ActionLabel, ActionPanel, Age, Availability, AvailabilityFoot, AvailabilityHead,
  BoatCheckbox, BoatInterest, BoatLine, BoatName, BoatState, BookingState, CardActions, CustomerActions,
  CustomerLine, DetailGrid, Entry, EntryMain, HeaderRow, InfoPair, InlineButton, Kicker, LastAction, Muted,
  NoteBody, NoteHeading, NotesColumn, PanelMeta, PrimaryButton, ReferenceRow, SecondaryButton,
  Tag, TaskActions, TaskBody, TaskTitle, TextButton, TripDate, TripLine, TripStat, UnreadBadge, Warning,
} from "./styles";
import { baseURL } from "index";
import {
  boatCheckFor, boatCheckLabel, bookingAge, boatNames, cruiseDay, cruiseTime, formatWhen, friendlyEnum, groupedBoats,
  lastAction, latestTask, leadName, listOrUnavailable, packageNames, quotedBoatNames, savedBoatChecks, selectedItemIds,
  stageLabel, taskSpec, taskState, cleanDescription,
} from "./model";

function Field({ label, value }) {
  if (!value) return null;
  return <InfoPair><span>{label}</span><strong>{value}</strong></InfoPair>;
}

function Badge({ count }) {
  if (!Number.isFinite(count) || count <= 0) return null;
  const label = count > 99 ? "99+" : count;
  return <UnreadBadge aria-label={`${count} unread messages`}>{label}</UnreadBadge>;
}

function RatingLink({ booking, button }) {
  if (!booking.reference) return null;
  const href = `${baseURL}/rating/?ref=${encodeURIComponent(booking.reference)}`;
  const Button = button ? SecondaryButton : TextButton;
  return <Button component="a" href={href} target="_blank" rel="noopener noreferrer">Rating link</Button>;
}

export default function BookingCard({
  booking, tasksByBooking, stagesByBooking, unreadByBooking, stages, today, now, expanded, selectedByBooking,
  historyAvailable, onAction, onToggle, onSelectBoat,
}) {
  const task = latestTask(booking, tasksByBooking, stagesByBooking);
  const spec = taskSpec(task?.type);
  const status = taskState(booking, tasksByBooking, stagesByBooking);
  const count = unreadByBooking.get(booking.id)?.count || 0;
  const boats = groupedBoats(booking);
  const selected = new Set(selectedItemIds(booking, selectedByBooking));
  const checks = savedBoatChecks(booking, tasksByBooking);
  const name = leadName(booking.lead) || "Name unavailable";
  const note = booking.notes;
  const day = cruiseDay(booking.cruiseDate);
  const time = cruiseTime(booking.cruiseDate);
  const age = bookingAge(booking.createdAt, now);
  const action = lastAction(booking);
  const email = booking.lead?.email;
  const phone = booking.lead?.phone;
  const missingSkippers = boats.filter((boat) => boat.skipperRequired && !boat.skippers.length).map((boat) => boat.name);

  return (
    <Entry>
      <EntryMain>
        <div>
          <HeaderRow>
            <ReferenceRow>
              <TextButton type="button" onClick={() => onAction("booking", booking)}>{booking.reference}</TextButton>
              <Muted>Created: <time dateTime={booking.createdAt}>{formatWhen(booking.createdAt)}</time></Muted>
              {age ? <Age>{age}</Age> : null}
              <Badge count={count} />
            </ReferenceRow>
            <TextButton type="button" aria-label={`Edit booking for ${name}`} onClick={() => onAction("edit", booking)}>
              <EditIcon style={{ fontSize: 18 }} />
            </TextButton>
          </HeaderRow>
          <CustomerLine>
            <InlineButton type="button" onClick={() => onAction("lead", booking)}>
              <PersonIcon style={{ fontSize: 16 }} /><span>{name}</span>
            </InlineButton>
            {email ? (
              <InlineButton type="button" onClick={() => onAction("lead", booking)}>
                <EmailIcon style={{ fontSize: 16 }} /><span>{email}</span>
              </InlineButton>
            ) : null}
            {phone ? (
              <InlineButton type="button" onClick={() => onAction("phone", booking)}>
                <PhoneIcon style={{ fontSize: 16 }} /><span>{phone}</span>
              </InlineButton>
            ) : null}
          </CustomerLine>
          <TripLine>
            <TripDate className={day && day < today ? "expired" : ""}>
              {formatWhen(day)}{time ? ` · ${time}` : ""}
            </TripDate>
            <TripStat><b>{booking.groupSize ?? "Unavailable"}</b> guests</TripStat>
            <TripStat><b>{booking.hours ?? "Unavailable"}</b> hours</TripStat>
            <TripStat>Source: <b>{booking.source ?? "Unavailable"}</b></TripStat>
          </TripLine>
          <BoatInterest>Interested boats: <strong>{boatNames(booking)}</strong></BoatInterest>
          <BookingState>{stageLabel(booking, stages, stagesByBooking)}</BookingState>
          {missingSkippers.length ? <Warning>Skipper missing</Warning> : null}
          {count > 0 ? (
            <div>
              <TextButton type="button" onClick={() => onAction("conversation", booking)}>
                <Badge count={count} /> Unread messages
              </TextButton>
            </div>
          ) : null}
          <CustomerActions>
            <details>
              <summary>View more details</summary>
              <DetailGrid>
                <Field label="Created" value={formatWhen(booking.createdAt)} />
                <Field label="Age" value={age} />
                <Field label="Catering" value={friendlyEnum(booking.catering)} />
                <Field label="Event" value={friendlyEnum(booking.event)} />
                <Field label="Interested packages" value={listOrUnavailable(packageNames(booking))} />
                <Field label="Quoted boats" value={listOrUnavailable(quotedBoatNames(booking))} />
                <Field label="Availability request sent" value={booking.availabilityCheckSentOn ? formatWhen(booking.availabilityCheckSentOn, true) : "Unavailable"} />
                <Field label="Rating SMS sent" value={booking.reviewRequestSentOn ? formatWhen(booking.reviewRequestSentOn, true) : "Unavailable"} />
              </DetailGrid>
              <RatingLink booking={booking} />
            </details>
            {historyAvailable ? <SecondaryButton onClick={() => onAction("history", booking)}>Lead history</SecondaryButton> : null}
          </CustomerActions>
        </div>
        <div>
          <Kicker>
            <span>Latest task</span>
            {task ? <Tag>{spec?.label || task.type}</Tag> : null}
          </Kicker>
          {task ? (
            <>
              <TaskTitle>{task.name || spec?.title}</TaskTitle>
              <TaskBody>{cleanDescription(task.description)}</TaskBody>
              <Muted style={{ display: "block", marginTop: 9 }}>Created {formatWhen(task.createdAt, true)}</Muted>
              <TaskActions>
                <PrimaryButton onClick={() => onAction(spec ? (spec.kind === "message" ? "message" : "new-options") : "booking", booking)}>
                  {spec?.action || "Open booking"} <ArrowForwardIcon style={{ fontSize: 16 }} />
                </PrimaryButton>
                {["newBookingFollowup", "optionsSeenNoQuote"].includes(task.type) ? (
                  <SecondaryButton onClick={() => onAction("new-quote", booking)}>Create quote</SecondaryButton>
                ) : null}
                {task.type === "newEnquiryFollowup" ? (
                  <>
                    <SecondaryButton onClick={() => onAction("enquiry", booking)}>View enquiry</SecondaryButton>
                    <SecondaryButton onClick={() => onAction("spam", booking)}>Manage enquiry</SecondaryButton>
                  </>
                ) : null}
                <SecondaryButton onClick={() => onAction("task-done", booking)}>Mark as done</SecondaryButton>
              </TaskActions>
            </>
          ) : (
            <>
              <TaskTitle>{status === "done" ? "Task completed" : status === "none" ? "No current task" : "Task details unavailable"}</TaskTitle>
              {status === "done" ? <TaskBody>A new task will appear when one is created.</TaskBody> : null}
              {status === "unavailable" ? <TaskBody>Task details could not be loaded.</TaskBody> : null}
            </>
          )}
          <Availability>
            <AvailabilityHead>
              <span><DirectionsBoatIcon style={{ fontSize: 16, verticalAlign: "text-bottom" }} /> Boat availability</span>
              <TextButton type="button" onClick={() => onAction("add-boat", booking)}>+ Add boat</TextButton>
            </AvailabilityHead>
            {boats.map((boat) => {
              const check = boatCheckFor(boat, checks.checks);
              const state = boatCheckLabel(check);
              const all = boat.itemIds.length > 0 && boat.itemIds.every((id) => selected.has(id));
              const partial = !all && boat.itemIds.some((id) => selected.has(id));
              return (
                <BoatLine key={boat.itemIds[0] || boat.name}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <BoatCheckbox
                      checked={all}
                      indeterminate={partial}
                      disabled={!boat.itemIds.length}
                      onChange={(event) => onSelectBoat(booking, boat.itemIds[0], event.target.checked)}
                      inputProps={{ "aria-label": `Include ${boat.name} in options or quote` }}
                    />
                    <BoatName>{boat.name}</BoatName>
                  </label>
                  <BoatState className={state.tone}>{state.label}</BoatState>
                </BoatLine>
              );
            })}
            <AvailabilityFoot>
              <span>
                {checks.error ? "Saved check unavailable" : checks.checks.length ? `Last saved check ${formatWhen(checks.task.createdAt, true)}` : "Check before sending options"}
              </span>
              <SecondaryButton onClick={() => onAction("availability", booking)}>Check availability</SecondaryButton>
            </AvailabilityFoot>
          </Availability>
        </div>
        <NotesColumn>
          <NoteHeading>
            <span>Notes</span>
            <TextButton type="button" aria-label={`Edit notes for ${name}`} onClick={() => onAction("note", booking)}>
              <EditIcon style={{ fontSize: 16 }} />
            </TextButton>
          </NoteHeading>
          <NoteBody>
            {note || <span style={{ color: "#888888" }}>No notes yet</span>}
          </NoteBody>
          <TextButton type="button" style={{ marginTop: 7 }} onClick={() => onAction("note", booking)}>
            {note ? "Edit note" : "Add note"}
          </TextButton>
          <CardActions>
            <PrimaryButton onClick={() => onAction("message", booking)}>Quick message</PrimaryButton>
            <SecondaryButton onClick={() => onToggle(booking.id)} aria-expanded={expanded}>
              Actions <ExpandMoreIcon style={{ fontSize: 16, transform: expanded ? "rotate(180deg)" : "none" }} />
            </SecondaryButton>
          </CardActions>
          {action ? <LastAction>{action}</LastAction> : null}
        </NotesColumn>
      </EntryMain>
      {expanded ? (
        <ActionPanel>
          <ActionGrid>
            <div>
              <ActionLabel>Booking</ActionLabel>
              <ActionButtons>
                <SecondaryButton onClick={() => onAction("edit", booking)}>Edit booking</SecondaryButton>
                <SecondaryButton onClick={() => onAction("options", booking)}>Manage options</SecondaryButton>
                <SecondaryButton onClick={() => onAction("quotes", booking)}>Manage quotes</SecondaryButton>
                <SecondaryButton onClick={() => onAction("merge", booking)}>Merge with another</SecondaryButton>
                <SecondaryButton className="danger" onClick={() => onAction("delete", booking)}>Delete booking</SecondaryButton>
              </ActionButtons>
            </div>
            <div>
              <ActionLabel>Availability and crew</ActionLabel>
              <ActionButtons>
                <SecondaryButton onClick={() => onAction("availability-request", booking)}>Send availability check</SecondaryButton>
                <SecondaryButton onClick={() => onAction("availability", booking)}>Check availability</SecondaryButton>
                <SecondaryButton onClick={() => onAction("booking-availability", booking)}>
                  {booking.available === true ? "Mark as unavailable" : booking.available === false ? "Mark as available" : "Booking availability"}
                </SecondaryButton>
                <SecondaryButton onClick={() => onAction("crew", booking)}>Manage crew</SecondaryButton>
              </ActionButtons>
            </div>
            <div>
              <ActionLabel>Communication</ActionLabel>
              <ActionButtons>
                <SecondaryButton onClick={() => onAction("conversation", booking)}>Open conversation</SecondaryButton>
                <SecondaryButton onClick={() => onAction("message", booking)}>Quick message</SecondaryButton>
                {historyAvailable ? <SecondaryButton onClick={() => onAction("history", booking)}>View history</SecondaryButton> : null}
                <RatingLink booking={booking} button />
              </ActionButtons>
            </div>
          </ActionGrid>
          <DetailGrid style={{ marginTop: 16, paddingTop: 10, borderTop: "1px solid #ededed" }}>
            <Field label="Created" value={formatWhen(booking.createdAt)} />
            <Field label="Age" value={age} />
            <Field label="Source" value={booking.source} />
            <Field label="Last action" value={action} />
            <Field label="Availability request sent" value={booking.availabilityCheckSentOn ? formatWhen(booking.availabilityCheckSentOn, true) : "Unavailable"} />
            <Field label="Rating SMS sent" value={booking.reviewRequestSentOn ? formatWhen(booking.reviewRequestSentOn, true) : "Unavailable"} />
            <Field label="Interested packages" value={listOrUnavailable(packageNames(booking))} />
            <Field label="Quoted boats" value={listOrUnavailable(quotedBoatNames(booking))} />
            <Field label="Catering" value={friendlyEnum(booking.catering)} />
            <Field label="Event" value={friendlyEnum(booking.event)} />
          </DetailGrid>
          <PanelMeta>
            <TextButton type="button" onClick={() => onAction("reference", booking)}>Copy booking reference</TextButton>
          </PanelMeta>
        </ActionPanel>
      ) : null}
    </Entry>
  );
}
